import { Component, Show, For } from "solid-js";
import { createSortable, transformStyle } from "@thisbeyond/solid-dnd";
import { Avatar } from "../ui";
import { TypeIcon } from "../work-items/TypeIcon";
import { PriorityMeter } from "../work-items/PriorityMeter";
import type { WorkItem, WorkItemType, EstimateUnit } from "../../api";
import { useProjectPrefix } from "../../contexts/ProjectContext";
import { formatEstimate } from "../../utils/estimate";

export interface WorkItemCardProps {
  item: WorkItem;
  itemType?: WorkItemType;
  childCount?: number;
  parentTitle?: string;
  overlay?: boolean;
  /** Whether the card's context menu is currently open, for aria-expanded on the trigger. */
  menuOpen?: boolean;
  onClick?: (item: WorkItem) => void;
  onContextMenu?: (
    item: WorkItem,
    x: number,
    y: number,
    trigger: HTMLElement | null,
  ) => void;
}

/**
 * WorkItemCard - Draggable card displayed on the Kanban board
 *
 * Features:
 * - Displays item ID and title
 * - Type icon with color
 * - Priority indicator
 * - Assignee avatar
 * - Labels (truncated to first 2)
 * - Click to open detail view
 * - Draggable for column transitions
 */
export const WorkItemCard: Component<WorkItemCardProps> = (props) => {
  // Only set up sortable when rendered inside a SortableProvider (not in DragOverlay)
  const sortable = props.overlay ? undefined : createSortable(props.item.id);

  const typeName = () =>
    props.item.item_type?.name ?? props.itemType?.name ?? "Task";

  // Generate a short display ID (e.g., "PRJ-42") from sequential item_number
  const prefix = useProjectPrefix();
  const displayId = () => {
    return `${prefix()}-${props.item.item_number}`;
  };

  const displayLabels = () => props.item.labels?.slice(0, 2) ?? [];
  const hasMoreLabels = () => (props.item.labels?.length ?? 0) > 2;

  const handleClick = (e: MouseEvent) => {
    // Don't trigger click when dragging
    if ((e.target as HTMLElement).closest("[data-dragging]")) return;
    props.onClick?.(props.item);
  };

  const handleContextMenu = (e: MouseEvent) => {
    if (!props.onContextMenu) return;
    e.preventDefault();
    // A right-click has no persistent focus target to restore later.
    props.onContextMenu(props.item, e.clientX, e.clientY, null);
  };

  const handleActionsClick = (e: MouseEvent) => {
    e.stopPropagation();
    const trigger = e.currentTarget as HTMLElement;
    const rect = trigger.getBoundingClientRect();
    props.onContextMenu?.(props.item, rect.left, rect.bottom, trigger);
  };

  return (
    <div
      ref={sortable?.ref}
      {...(sortable?.dragActivators ?? {})}
      class={`group bg-surface rounded border border-line py-2.25 px-2.5 cursor-pointer hover:border-line-strong transition-colors duration-150 ${sortable?.isActiveDraggable ? "opacity-40 border-ink-2" : ""}`}
      style={sortable ? transformStyle(sortable.transform) : undefined}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      data-dragging={sortable?.isActiveDraggable ? "true" : undefined}
      data-board-card={props.overlay ? undefined : ""}
    >
      {/* Header: ID and Priority */}
      <div class="flex items-center justify-between mb-2">
        <div class="flex items-center gap-2">
          <TypeIcon typeName={typeName()} />
          <span class="sr-only">{typeName()}</span>
          <span
            class="font-mono text-xs tabular-nums text-ink-2"
            data-display-id={displayId()}
          >
            <span class="font-normal">{prefix()}-</span>
            <span class="font-semibold">{props.item.item_number}</span>
          </span>
          <Show when={props.item.is_blocked}>
            <span
              aria-label="Blocked"
              title="Blocked by another item"
              class="inline-flex items-center text-signal-red"
            >
              <svg
                class="w-3.5 h-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="12" cy="12" r="9" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </span>
          </Show>
        </div>
        <div class="flex items-center gap-1">
          <PriorityMeter priority={props.item.priority} />
          <Show when={props.onContextMenu}>
            <button
              type="button"
              aria-label="Card actions"
              aria-haspopup="menu"
              aria-expanded={props.menuOpen ?? false}
              title="Card actions"
              class="opacity-0 group-hover:opacity-100 focus:opacity-100 p-0.5 text-ink-3 hover:text-ink hover:bg-sunken rounded-sm transition-colors duration-150 focus-ring hit-area"
              onClick={handleActionsClick}
              // stopPropagation here suppresses the card's solid-dnd drag activator: dragActivators
              // is applied via the JSX spread, and Solid's delegated pointerdown walk honours
              // cancelBubble. This would NOT work with the `use:sortable` directive form, which
              // attaches a native listener directly.
              onPointerDown={(e) => e.stopPropagation()}
            >
              <svg
                class="w-3.5 h-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="5" cy="12" r="1" />
                <circle cx="12" cy="12" r="1" />
                <circle cx="19" cy="12" r="1" />
              </svg>
            </button>
          </Show>
        </div>
      </div>

      {/* Depth marker + parent breadcrumb */}
      <Show when={props.item.depth > 0 || props.parentTitle}>
        <p class="flex items-center gap-1 text-2xs text-ink-3 truncate mb-0.5">
          <Show when={props.item.depth > 0}>
            <span
              role="img"
              aria-label={`Depth ${props.item.depth}`}
              class="font-mono text-2xs leading-none"
            >
              {"›".repeat(props.item.depth)}
            </span>
          </Show>
          <Show when={props.parentTitle}>
            <span class="truncate">{props.parentTitle}</span>
          </Show>
        </p>
      </Show>

      {/* Title */}
      <h4 class="text-sm font-medium text-ink line-clamp-2 mb-2">
        {props.item.title}
      </h4>

      {/* Footer: Labels, estimate, child count, assignee */}
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-1.5 flex-wrap min-w-0">
          <For each={displayLabels()}>
            {(labelId) => (
              <span class="font-mono text-2xs text-ink-3 truncate max-w-[72px]">
                #{labelId.slice(-4)}
              </span>
            )}
          </For>
          <Show when={hasMoreLabels()}>
            <span class="font-mono text-2xs text-ink-3">
              +{(props.item.labels?.length ?? 0) - 2}
            </span>
          </Show>
          <Show
            when={props.item.estimate_value != null && props.item.estimate_unit}
          >
            <span
              data-estimate
              class="font-mono text-2xs tabular-nums px-1 rounded-sm bg-sunken border border-line text-ink-2"
            >
              {formatEstimate(
                Number(props.item.estimate_value),
                props.item.estimate_unit as EstimateUnit,
              )}
            </span>
          </Show>
          <Show when={props.childCount && props.childCount > 0}>
            <span
              class="flex items-center gap-0.5 text-ink-3"
              title={`${props.childCount} subtask${props.childCount === 1 ? "" : "s"}`}
            >
              <svg
                class="w-3 h-3"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M4 6h16M4 12h8m-8 6h16"
                />
              </svg>
              <span class="text-2xs">{props.childCount}</span>
            </span>
          </Show>
        </div>

        <Show when={props.item.assignee_id}>
          <Avatar
            name={props.item.assignee_id ?? "Unassigned"}
            size="xs"
            class="flex-shrink-0"
          />
        </Show>
      </div>
    </div>
  );
};

export default WorkItemCard;
